import { v4 as uuidv4 } from "uuid";
import bookImagePlaceholder from "@circle-react-shared/BuilderUI/Assets/icons/book_placeholder_big.png";
import calendarImagePlaceholder from "@circle-react-shared/BuilderUI/Assets/icons/calendar_placeholder_big.png";
import supportImagePlaceholder from "@circle-react-shared/BuilderUI/Assets/icons/support_placeholder_big.png";
import usersImagePlaceholder from "@circle-react-shared/BuilderUI/Assets/icons/users_placeholder_big.png";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import { createCardBlock } from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/layoutBlockHelpers/cardBlockHelper";
import { createImageBlock } from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/mediaBlockHelpers";
import { secondaryBlockDefaultStyles } from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/stylesHelpers";
import {
  createButtonBlock,
  createH2Block,
  createPBlock,
} from "@circle-react-shared/BuilderUI/DefaultBlocks/utils/textBlocksHelpers";
import { getBackgroundImageOptions } from "@circle-react-shared/BuilderUI/utils/backgroundHelpers";
import {
  getMarginOptions,
  getPaddingOptions,
} from "@circle-react-shared/BuilderUI/utils/boxModelStyleHelpers";
import { getBackgroundColorOptions } from "@circle-react-shared/BuilderUI/utils/buttonStyleHelpers";
import { getBorderRadiusOptions } from "@circle-react-shared/BuilderUI/utils/imgStyleHelpers";
import { getSizingOptions } from "@circle-react-shared/BuilderUI/utils/sizeStyleHelpers";
import placeholderImage from "./homepage_block_7_default_image.png";

const cards = [
  {
    image: {
      signedId: "",
      placeholder: usersImagePlaceholder,
    },
    h5Text: "Proven course launch framework",
    pText:
      "Follow a step-by-step launch plan built to reduce guesswork and increase momentum. From defining your offer to delivering your first cohort, this framework gives you the clarity, structure, and support to launch with confidence—and sustainability.",
  },
  {
    image: {
      signedId: "",
      placeholder: calendarImagePlaceholder,
    },
    h5Text: "Student-centered curriculum",
    pText:
      "Design learning experiences that actually move people forward. With Elevate's curriculum templates, you'll map outcomes, create engaging content flows, and guide your students through a journey that's not just informative—but transformational.",
  },
  {
    image: {
      signedId: "",
      placeholder: supportImagePlaceholder,
    },
    h5Text: "Systems that support your scale",
    pText:
      "Build a course backend that works for you—not against you. From automated onboarding to content delivery workflows, you'll set up systems that streamline your teaching and give your students a seamless experience, without the tech overload.",
  },
  {
    image: {
      signedId: "",
      placeholder: bookImagePlaceholder,
    },
    h5Text: "Positioning, pricing & offer strategy",
    pText:
      "Your knowledge is valuable—but how you package it matters. Learn how to position your course for the right audience, choose a pricing model that reflects your offer's impact, and create an offer that scales with clarity, not complexity.",
  },
] as const;

export const defaultData: BaseBlock = {
  id: uuidv4(),
  type: "EvergreenHomepageBlock7",
  props: {
    header: {
      prop: "children",
      type: "children",
      value: [
        createH2Block({
          text: "Built for clarity, designed to scale",
        }),
        createPBlock({
          text: "This course blends curriculum architecture, business strategy, and delivery systems—so you can focus on creating impact, not just content.",
        }),
      ],
    },
    content: {
      prop: "children",
      type: "children",
      value: [
        createImageBlock({
          signedId: "",
          placeholder: placeholderImage,
          styles: {
            borderRadius: getBorderRadiusOptions({
              defaultValue: {
                border_left_top_radius: 20,
                border_left_bottom_radius: 20,
                border_right_top_radius: 20,
                border_right_bottom_radius: 20,
              },
            }),
            sizing: getSizingOptions({
              defaultValue: {
                maxHeight: "none",
                maxWidth: "none",
                width: "100%",
                height: "100%",
                minHeight: "auto",
                minWidth: "auto",
              },
            }),
          },
        }),
      ],
    },
    cards: {
      prop: "children",
      type: "children",
      value: cards.map(card => createCardBlock(card)),
    },
    footer: {
      prop: "children",
      type: "children",
      value: [
        createButtonBlock({
          text: "Enroll now",
          href: "#",
        }),
        createPBlock({
          text: "Limited spots available. Applications close at the end of the month.",
          paragraphStyle: "p3",
        }),
      ],
    },
    styles: {
      prop: "styles",
      type: "styles",
      value: {
        backgroundColor: getBackgroundColorOptions({
          defaultValue: secondaryBlockDefaultStyles.backgroundColor,
        }),
        backgroundImage: getBackgroundImageOptions(),
        padding: getPaddingOptions({
          defaultValue: secondaryBlockDefaultStyles.padding,
        }),
        margin: getMarginOptions({
          defaultValue: secondaryBlockDefaultStyles.margin,
        }),
      },
    },
  },
};
